import { FiniteStateMachine, type ActionFn } from 'runed'

import { appState, MAP_BREADCRUMBS_CONTEXT } from './appstate.svelte'
// FIXME: can we reuse the websocket? Do we need a separate instance?
// should we bind the websocket in appState and recycle from there?
import { webSocket } from '$lib/websocket.svelte'
import { type MapMarker } from '$mapapp/MapApp/MapView/Marker/BasicMarker.model'
import { LngLatBounds, type GeoJSONSource } from 'maplibre-gl'
import { SLIDE_IN_WIDTH } from '$mapapp/config'
import circle from '@turf/circle'
import type { IMessage } from '@stomp/stompjs'
import type {
	GenericRequestMatchMarkerMessage,
	LocationType,
	LotMatchPartner,
	MatchCoordinates,
	MatchingOverview,
	MatchOverviewData,
	MiniLotMaterialProperties,
	MiniLotRequestMatchMarkerMessage,
	PlatformRequestMailInfo,
	PopMatchPartner,
	ReturnFreightData
} from '../data/data.model'
import { addBreadcrumb, type SetEvents as FSMSetEvents } from './fsm.svelte'
import { computePolylineRoute } from '$mapapp/utils/routes_api'
import type { LngLatLike } from 'maplibre-gl'
import { getLotInformation, getPopInformation } from '$mapapp/data/data.svelte'
import { LOT_PROVIDES_MATERIAL, LotType } from '$types/enums'
import { SvelteMap, SvelteSet } from 'svelte/reactivity'
import { fetchUtils } from '$utils/fetchUtils'
import { type MiniLot, miniLotToLot, miniLotToMatchRequest } from '$mapapp/types/miniLot'
import { breadcrumbs } from '$lib/organisms/Breadcrumbs/breadcrumbs.svelte'
import { DEFAULT_RADIUS_KM } from './matching-config'
import { MatchingResultsState } from '$mapapp/MapApp/SlideIn/Views/Matching/MatchingResults/matchingResultsState.svelte'

type States = 'results' | 'result' | 'secondary'
type MatchingSetEvents = 'set_results' | 'set_result' | 'set_secondary'
export type MatchingOptions = {
	profileId?: string
	radius?: { from: number; to: number }
	miniLot?: MiniLot
}

/**
 * canonical markerId built from a partner's type+id.
 *
 * SSOT for the `matches` map key AND lookups into `appState.allMarkers`. both
 * must use this format so internal lookups intersect with the global marker
 * registry without an intermediate transform. backend DTO contract:
 * `MatchOverviewPartnerDto` exposes `objType: 'lot' | 'pop'` and `objId: string`
 */
const markerIdFor = (objType: string, objId: string): string => `${objType}-${objId}`

/** id of the maplibre GeoJSON source rendered by LayerMatchingRadius. Exported so
 * callers (e.g. fitToRadius) can look up bounds without re-importing the layer. */
export const MATCHING_RADIUS_SRC = 'matching-radius-src'

export class Matching {
	readonly #selectMatch = (...args: unknown[]): States | void => {
		const event = args[0] as { id: string; type: string } | undefined
		const found = event ? this.matches.get(markerIdFor(event.type, event.id)) : undefined

		if (found) {
			/**
			 * replace, don't stack: only pop when the previous crumb is itself `Match`.
			 *
			 * the old guard `if (this.selectedMatch)` popped whatever crumb was at the
			 * tail — including `Standort` / `Position` etc. that were pushed by an
			 * intervening secondary-view click — which corrupted the chain. by
			 * matching on title we only collapse consecutive `Match` selections.
			 */
			const lastCrumbTitle = breadcrumbs.get(MAP_BREADCRUMBS_CONTEXT).at(-1)?.title
			if (lastCrumbTitle === 'Match') {
				breadcrumbs.pop(MAP_BREADCRUMBS_CONTEXT)
			}

			this.selectedMatch = found
			this.#fetchAndDisplayRoute(found)
			// addBreadcrumb(`Match: ${found.partner.objType} ${found.partner.objId}`)
			addBreadcrumb('Match')
			return 'result'
		}
	}

	/**
	 * Match might get selected before the partner marker arrives in {@link appState.allMarkers}.
	 * Kept here so a follower (e.g. LayerMatching's reactive effect on `matchMarkers`)
	 * can retry the route fetch once the partner streams in.
	 */
	pendingRouteMatch: MatchOverviewData | null = $state(null)

	/** Controls visibility of the RouteInfoOverlay on the map. Opened only by clicking the route polyline. */
	routingInfoVisible: boolean = $state(false)

	#fetchAndDisplayRoute(match: MatchOverviewData) {
		const partnerMarker = appState.allMarkers.get(
			markerIdFor(match.partner.objType, match.partner.objId)
		)

		if (!this.referenceMarker || !partnerMarker) {
			// stash for retry — LayerMatching watches matchMarkers/allMarkers and calls
			// retryPendingRoute() when the partner becomes available.
			this.pendingRouteMatch = match
			return
		}

		this.pendingRouteMatch = null
		const origin = { lat: this.referenceMarker.lat, lon: this.referenceMarker.lon } as LngLatLike
		const destination = { lat: partnerMarker.lat, lon: partnerMarker.lon } as LngLatLike

		computePolylineRoute(origin, destination)
			.then(({ polyline }) => {
				if (polyline && this.fsm.current === 'result') {
					appState.route.setCoordinates(polyline)
					this.routingInfoVisible = false
					this.#fitToRouteBounds(polyline)
				}
			})
			.catch((err) => {
				console.error('failed to fetch route for match', err)
			})
	}

	#fitToRouteBounds(polyline: [number, number][], secondaryWidthPx: number = 400) {
		const bounds = new LngLatBounds()
		for (const coord of polyline) {
			bounds.extend(coord)
		}

		appState.map?.fitBounds(bounds, {
			padding: {
				top: 80,
				bottom: 80,
				left:
					(appState.is_desktop && appState.isSidebarOpened
						? SLIDE_IN_WIDTH + secondaryWidthPx
						: 0) + 80,
				right: 80
			},
			maxZoom: 14,
			animate: true
		})
	}

	/**
	 * Re-fit the camera to the current route, accounting for the live width of the secondary
	 * slide-in (it collapses to 48px). No-op when there is no active match or route — safe to
	 * call from UI handlers that don't track those preconditions themselves.
	 */
	recenterRoute(secondaryWidthPx: number = 400): void {
		const polyline = appState.route.coordinates
		if (!polyline || polyline.length === 0) return
		this.#fitToRouteBounds(polyline, secondaryWidthPx)
	}

	/**
	 * Re-attempt a route fetch that was deferred because the partner marker hadn't yet
	 * landed in {@link appState.allMarkers}. No-op when nothing is pending or when the
	 * partner is still missing — callers can fire this repeatedly without harm.
	 */
	/**
	 * Fit map viewport to the matching-radius annulus. Bounds are derived from the reference
	 * marker + confirmedRadius rather than the rendered source — the source is updated through
	 * a tween, so reading its current geometry right after refetch() would race against the
	 * easing and produce stale bounds. Falls back to the live source when the marker isn't
	 * available yet (e.g. miniLot before the server returns coords).
	 */
	async fitToRadius(): Promise<boolean> {
		const lon = Number(this.referenceMarker?.lon)
		const lat = Number(this.referenceMarker?.lat)
		const radius = this.confirmedRadius
		if (appState.map && Number.isFinite(lon) && Number.isFinite(lat) && radius && radius.to > 0) {
			const poly = circle([lon, lat], radius.to, { units: 'kilometers', steps: 64 })
			const ring = (poly.geometry as { coordinates: number[][][] }).coordinates[0]
			const lngs = ring.map((c) => c[0])
			const lats = ring.map((c) => c[1])
			const bounds = new LngLatBounds(
				[Math.min(...lngs), Math.min(...lats)],
				[Math.max(...lngs), Math.max(...lats)]
			)
			appState.handleFitBounds(bounds, { padding: 80, animate: true }, true)
			return true
		}
		// fallback: read from the live source (legacy path for miniLot etc.)
		const source = appState.map?.getSource(MATCHING_RADIUS_SRC) as GeoJSONSource | undefined
		if (!source) return false
		const bounds = await source.getBounds()
		if (bounds.isEmpty()) return false
		appState.handleFitBounds(bounds, { padding: 80, animate: true }, true)
		return true
	}

	retryPendingRoute(): void {
		const pending = this.pendingRouteMatch
		if (!pending) return
		const partnerMarker = appState.allMarkers.get(
			markerIdFor(pending.partner.objType, pending.partner.objId)
		)
		if (!partnerMarker || !this.referenceMarker) return
		this.#fetchAndDisplayRoute(pending)
	}

	// setter for proxy / secondary state

	readonly #clearTempState = () => {
		this.temp_selectedId = null
		this.temp_selectedMarker = undefined
		this.temp_selectedType = undefined
		this.temp_selectedChildrenIds = []
		this.temp_selectedChildren = undefined
	}

	readonly #set_secondary = (...args: unknown[]): States | void => {
		const event = args[0] as { id: string; children?: string[] } | undefined
		if (!event?.id) {
			console.error('id is required')
			return
		}

		// HINT: close popup so slide-in is single source of truth
		// for the secondary view
		appState.closePopup()

		// remember the state we entered secondary from so goBack can restore it
		// chained secondary->secondary keeps the original entry context
		if (this.fsm.current !== 'secondary') {
			this.secondaryFromState = this.fsm.current as 'results' | 'result'
		}

		if (event.id.startsWith('cluster-')) {
			this.temp_selectedId = event.id
			this.temp_selectedMarker = undefined
			this.temp_selectedType = 'cluster'
			this.temp_selectChildren(event.children ?? [])
		} else {
			this.temp_selectMarker(event.id)
			this.temp_selectedType = this.temp_selectedMarker?.type
			this.temp_selectChildren([])
		}
		return 'secondary'
	}

	fsm = new FiniteStateMachine<States, MatchingSetEvents>('results', {
		results: {
			set_result: this.#selectMatch,
			set_secondary: this.#set_secondary as ActionFn<States>
		},
		result: {
			set_result: this.#selectMatch,
			set_results: () => {
				appState.route.clearCoordinates()
				return 'results'
			},
			set_secondary: this.#set_secondary as ActionFn<States>
		},
		secondary: {
			_exit: () => {
				this.#clearTempState()
				appState.closePopup()
			},
			set_result: this.#selectMatch,
			set_results: () => {
				appState.route.clearCoordinates()
				return 'results'
			},
			set_secondary: this.#set_secondary as ActionFn<States>
		}
	})

	referenceId: string | undefined = $state(undefined)
	referenceMarker: MapMarker | undefined = $state(undefined)
	selectedMarkerType: 'pop' | 'lot' | 'miniLot' | null = $state(null)
	profileId: string | undefined = $state(undefined) // Optional: specific profile ID for Pop matching

	/** Distance range (km) for matching. Used by MatchingTable slider and set_matching payload. */
	radius: { from: number; to: number } = $state({ from: 0, to: DEFAULT_RADIUS_KM })
	/** Last radius committed via refetch(). Slider-only edits leave this unchanged so the radius
	 *  layer can render a pending-preview (dashed orange) until the user confirms. */
	confirmedRadius: { from: number; to: number } = $state({ from: 0, to: DEFAULT_RADIUS_KM })
	/** Sticky flag: user has touched the slider since the last refetch(). Stays true even if the
	 *  slider snaps back to a value equal to confirmedRadius — prevents the preview from flashing. */
	isRadiusPending: boolean = $state(false)

	markRadiusEdited() {
		this.isRadiusPending = true
	}

	matchReferenceData: LotMatchPartner | PopMatchPartner | undefined = $state(undefined)
	cleanupReferenceData: (() => void) | undefined // binding for destroying effect root

	// Matches from API (!= markers). key shape: see markerIdFor above
	matches: SvelteMap<string, MatchOverviewData> = $state(new SvelteMap())
	matchMarkerIds: SvelteSet<string> = $derived.by(
		() =>
			new SvelteSet(
				Array.from(this.matches.values(), (match) =>
					markerIdFor(match.partner.objType, match.partner.objId)
				)
			)
	)
	// TODO Check if relevant: array of markers, because we need to keep the order of matches
	matchMarkers: (MapMarker | undefined)[] = $derived.by(() =>
		Array.from(this.matches.values(), (match) =>
			appState.allMarkers.get(markerIdFor(match.partner.objType, match.partner.objId))
		)
	)
	matchesLoading: boolean = $derived(webSocket.isStreamBusy('/return/matches'))

	/** Shared ranking + active-tab state for the results list and the map markers. */
	resultsState = new MatchingResultsState(
		() => this.matches,
		() => this.matchReferenceData
	)

	returnFreights: SvelteMap<string, ReturnFreightData> = $state(new SvelteMap())
	returnFreightMarkers: (MapMarker | undefined)[] = $derived.by(() =>
		Array.from(this.returnFreights.values(), (returnFreight) =>
			appState.allMarkers.get(`lot-${returnFreight.id}`)
		)
	)

	selectedMatch: MatchOverviewData | undefined = $state(undefined)
	/** Gate for MatchingResults' "auto-select first match" effect. Reset to true on every fresh
	 *  match stream (constructor, refetch); flipped false in closeSelectedMatch so the user's
	 *  dismissal doesn't get immediately undone by the effect re-selecting the first row. */
	autoSelectFirst: boolean = $state(true)
	selectedMatchId: string | undefined = $derived.by(() =>
		this.selectedMatch
			? markerIdFor(this.selectedMatch.partner.objType, this.selectedMatch.partner.objId)
			: undefined
	)

	miniLot: MiniLot | undefined = $state(undefined)

	partnerMailInfoCache: Map<string, Promise<PlatformRequestMailInfo>> = $state(new SvelteMap())
	requestId: string = Date.now().toString()

	/**
	 * SECONDARY MODE
	 * - during matching we can interact with secondary markers
	 * - they trigger regular set_<type> events
	 * - we intercept in fsm.svelte.ts and invoke a matching.method()
	 * - however, since we can't mess with appstate state (selected marker, children etc)
	 * - we need to mirror / mock the state here
	 */

	// TODO possibly refactor as selectedMarker, then we can give all information to the slide in, the checks can happen via selectedMarker.id
	temp_selectedId: string | null = $state(null)
	temp_selectedMarker: MapMarker | undefined = $state(undefined)
	temp_selectedType: MapMarker['type'] | 'cluster' | undefined = $state(undefined)
	temp_selectedChildrenIds: string[] = $state([])
	temp_selectedChildren: MapMarker[] | undefined = $state(undefined)

	// matching state that preceded entry into 'secondary'; goBack uses it to restore
	secondaryFromState: 'results' | 'result' | undefined = $state(undefined)

	temp_selectMarker(id: string) {
		this.temp_selectedId = id

		if (!this.temp_selectedId || !appState.allMarkers.has(this.temp_selectedId)) {
			console.error('Marker not found', this.temp_selectedId)
			this.temp_selectedMarker = undefined
			return
		}

		this.temp_selectedMarker = appState.allMarkers.get(this.temp_selectedId)
	}

	temp_selectChildren(children: string[]) {
		// this.selectedChildren = children.map((child) => this.allMarkers.get(child))
		this.temp_selectedChildrenIds = children
		if (children.length > 0) {
			this.temp_selectedChildren = children.map(
				(child) => appState.allMarkers.get(child) as MapMarker
			)
		} else {
			this.temp_selectedChildren = undefined
		}
	}

	/**
	 * HINT: keep constructor narrow; ideally we just need an id and an options object
	 * currently, we have mixed id semantics (compound id type-id vs. split id type/id)
	 * we need additional values depending on special cases, such as matching from a profile or
	 * a different search radius
	 * Hence: options object for easier extentability of the constructor interface
	 * @example:
	 * - plain: new Matching(markerId, markerType)
	 * - with profileId: new Matching(markerId, markerType, { profileId: '123' })
	 * - with radius: new Matching(markerId, markerType, { radius: { from: 0, to: 1000 } })
	 * - with profileId and radius: new Matching(markerId, markerType, { profileId: '123', radius: { from: 0, to: 1000 } })
	 */
	// TODO: Document Minilot stuff
	constructor(
		markerType: 'pop' | 'lot' | 'miniLot',
		markerId?: string,
		options: MatchingOptions = {}
	) {
		this.referenceId = markerId
		if (markerType !== 'miniLot') {
			// miniLot has no pre-resolved marker; subscribeToMiniLotCoordinates
			// assigns referenceMarker after server returns coords
			this.referenceMarker = appState.selectedMarker
		}
		this.selectedMarkerType = markerType

		// FIXME: Only if there is a profile id, we can start the matching for markerType 'pop'. We do not yet allow matching
		// on all profiles of a pop at once.
		if (options?.profileId) {
			// TODO: make sure this is cleaned up when switching / resetting
			this.profileId = options.profileId
		}

		if (options?.radius) {
			this.radius = { from: options.radius.from, to: options.radius.to }
		}
		this.confirmedRadius = { from: this.radius.from, to: this.radius.to }

		if (options?.miniLot) {
			this.miniLot = options.miniLot
		}

		// HINT: we need to fetch the data for the reference marker with an abort signal
		// we did not want to rewrite the getReferenceMarkerData function to provide our own abort signal
		// Hence we're using a trick here
		// LEAVE it like this until we / Svelte provides a better solution for async in Svelte 5 classes
		this.cleanupReferenceData = $effect.root(() => {
			$effect(() => {
				void this.getReferenceData(markerId, markerType).then((data) => {
					this.matchReferenceData = data
				})
			})
		})

		this.#triggerMatching()
	}

	/**
	 * TODO: Document
	 * @private
	 */
	#triggerMatching() {
		this.subscribeToMatchMarkers()
		if (this.miniLot) {
			/**
			 * HINT: subscribe once per session here, not inside the fetch.
			 *
			 * each subscribeToMiniLotCoordinates call registers a fresh STOMP
			 * callback. the websocket layer does not deduplicate by destination,
			 * so subscribing per fetch would pile up callbacks and fire them
			 * all on every coordinate message.
			 */
			this.subscribeToMiniLotCoordinates()
			this.subscribeToMiniLotMaterialProperties()
			/**
			 * both subscriptions MUST be registered before the fetch, not after. addSubscription sends a
			 * STOMP SUBSCRIBE frame and startStreamRequest a request SEND frame on the same ordered wire,
			 * in call order. the /matchMarker request is what makes the server publish the one-shot
			 * materialProperties frame (+ its terminal endMessage), so fetching first races the SUBSCRIBE
			 * to the broker — the frame can be published before the subscription exists and is silently dropped
			 */
			this.fetchMatchMarkerFromMiniLot()
		} else {
			this.fetchMatchMarker()
		}
	}

	//  methods
	async getReferenceData(id: string | undefined, type: 'pop' | 'lot' | 'miniLot') {
		if (type === 'miniLot' && this.miniLot) {
			// full lot shape so LotHeader has volume/material/address; see docs/MINILOT.md
			return {
				...miniLotToLot(this.miniLot),
				providesMaterial: LOT_PROVIDES_MATERIAL.includes(this.miniLot.type),
				objType: 'lot',
				objId: 'minilot'
			} as LotMatchPartner
		}

		if (!id) {
			console.error('id is required')
			return undefined
		}

		if (!type) {
			console.error('type is required')
			return undefined
		}

		if (type === 'pop') {
			const pop = await getPopInformation(id)
			if (pop) {
				return {
					...pop,
					objId: pop.id,
					objType: 'pop',
					label: pop.label,
					providesMaterial: pop?.materialProvides?.length > 0
				} as unknown as PopMatchPartner
			}
		} else if (type === 'lot') {
			const lot = await getLotInformation(id)
			if (lot) {
				return {
					...lot,
					providesMaterial: LOT_PROVIDES_MATERIAL.includes(lot.type),
					objType: 'lot',
					objId: lot.id
				} as LotMatchPartner
			}
		}
	}

	setNextMatch() {
		if (!appState.isMatching || !this.selectedMatch) {
			return
		}

		const key = markerIdFor(this.selectedMatch.partner.objType, this.selectedMatch.partner.objId)
		const keys = [...this.matches.keys()]
		const currentIndex = keys.indexOf(key)

		if (currentIndex === -1) return

		const nextKey = keys[(currentIndex + 1) % keys.length] // wraps around
		const nextMatch = this.matches.get(nextKey)

		if (nextMatch) {
			this.fsm.send('set_result', { id: nextMatch.partner.objId, type: nextMatch.partner.objType })
		}
	}

	hasNextMatch() {
		if (!appState.isMatching || !this.selectedMatch || this.matches.size < 2) return false

		const key = markerIdFor(this.selectedMatch.partner.objType, this.selectedMatch.partner.objId)
		const keys = [...this.matches.keys()]
		const currentIndex = keys.indexOf(key)

		return currentIndex !== -1 && currentIndex + 1 < keys.length
	}

	async getPartnerMailInfo(
		popId: string,
		invalidateCache: boolean = false
	): Promise<PlatformRequestMailInfo | undefined> {
		const cached = this.partnerMailInfoCache.get(popId)
		if (cached && !invalidateCache) {
			return cached
		}

		const refId = this.matchReferenceData?.objId
		if (!refId || refId === 'minilot') {
			return undefined
		}

		const request = fetchUtils
			.get(`/api/platformRequest/lastMailInfos/${refId}/${popId}`, false)
			.catch((err) => {
				console.error('failed to fetch mail infos', err.message)
			})

		this.partnerMailInfoCache.set(popId, request)
		return request
	}

	/**
	 * Close the currently selected match and return to the results list.
	 *
	 * Mirror of the `result` branch in {@link handleMatchingHistory}, exposed as a
	 * direct call for UI that has a single source state (e.g. SecondarySlideIn's
	 * header chevron) and shouldn't route through history dispatch.
	 */
	closeSelectedMatch(): void {
		appState.closePopup()
		this.selectedMatch = undefined
		this.autoSelectFirst = false
		this.fsm.send('set_results') // action: appState.route.clearCoordinates() + transition to 'results'
		breadcrumbs.pop(MAP_BREADCRUMBS_CONTEXT)
	}

	// prototype of matching history + crossing boundary back into non-matching
	handleMatchingHistory() {
		if (!appState.isMatching) {
			return
		}

		if (!appState.matching) {
			return
		}

		if (appState.matching.fsm.current === 'results') {
			appState.closePopup()
			/**
			 * only marker states have a set_<state> handler in the global FSM
			 *
			 * for any other previous_state (e.g. 'idle' from a non-marker entry
			 * path) fall through to the reset branch below. dispatching
			 * set_idle would not match any handler, so the click on the back
			 * button would have no effect
			 */
			const MARKER_STATES = ['pop', 'lot', 'project', 'cluster', 'request']
			if (
				appState.previous_state &&
				MARKER_STATES.includes(appState.previous_state) &&
				appState.selectedId
			) {
				const setter = `set_${appState.previous_state}` as FSMSetEvents
				// pan back to the original marker at current zoom (no zoom snap).
				// dispatch with flyTo:false so the marker handler skips its own
				// handleFlyTo (which would snap to its default zoom 15).
				const currentZoom = appState.map?.getZoom()
				appState.fsm.send(setter as FSMSetEvents, {
					id: appState.selectedId,
					flyTo: false,
					modifier: 'exit_matching'
				})
				const marker = appState.allMarkers.get(appState.selectedId)
				if (marker && currentZoom !== undefined) {
					appState.handleFlyTo([marker.lon, marker.lat], currentZoom, true)
				}
				breadcrumbs.pop(MAP_BREADCRUMBS_CONTEXT)
				return
			}

			if (this.selectedMarkerType === 'miniLot') {
				breadcrumbs.pop(MAP_BREADCRUMBS_CONTEXT)
				appState.fsm.send('set_minilot', appState.matching.miniLot)
				return
			}

			breadcrumbs.pop(MAP_BREADCRUMBS_CONTEXT)
			appState.fsm.send('reset')
			return
		}

		if (appState.matching.fsm.current === 'secondary') {
			if (this.secondaryFromState === 'result' && this.selectedMatch) {
				appState.matching.fsm.send('set_result', {
					id: this.selectedMatch.partner.objId,
					type: this.selectedMatch.partner.objType
				})
			} else {
				appState.matching.fsm.send('set_results')
			}
			breadcrumbs.pop(MAP_BREADCRUMBS_CONTEXT)
			this.secondaryFromState = undefined
			return
		}

		if (appState.matching.fsm.current === 'result') {
			this.closeSelectedMatch()
		}
		// results can only be triggered when a marker is selected
		// hence just return to previous state
	}

	// websocket
	/**
	 * Subscribe to marker updates from the server. All the updates on markers are sent to the websocket via this route.
	 */
	subscribeToMatchMarkers() {
		webSocket.addSubscription('/return/matches', (message: IMessage) => {
			try {
				// matchReferenceData is resolved separately via getReferenceMarkerData()
				const data: MatchingOverview = JSON.parse(message.body)
				if (data.requestId !== this.requestId) return
				for (const match of data.matchOverviews) {
					this.matches.set(markerIdFor(match.partner.objType, match.partner.objId), match)
				}
				webSocket.onStreamMessage('/return/matches', data.requestId, data.endMessage)
			} catch (e) {
				console.error(e)
				throw new Error('Could not parse message body: ', { cause: e })
			}
		})
	}

	subscribeToMiniLotCoordinates() {
		webSocket.addSubscription('/return/minilot/coordinates', (message: IMessage) => {
			try {
				const data: MatchCoordinates = JSON.parse(message.body)
				if (data.requestId !== this.requestId) return
				let filterTypes: (LotType | LocationType)[] | undefined
				if (filterTypes) {
					filterTypes =
						this.matchReferenceData?.objType === 'lot'
							? [(this.matchReferenceData as LotMatchPartner).type]
							: (this.matchReferenceData as PopMatchPartner).types
				}
				this.referenceMarker = {
					markerId: 'minilot',
					lat: data.lat,
					lon: data.lon,
					type: 'lot',
					filterType: filterTypes
				} as MapMarker
				appState.selectedMarker = this.referenceMarker
				webSocket.onStreamMessage(
					'/return/minilot/coordinates',
					`${data.requestId}:minilot`,
					data.endMessage
				)
			} catch (e) {
				console.error(e)
				throw new Error('Could not parse message body', { cause: e })
			}
		})
	}

	subscribeToMiniLotMaterialProperties() {
		webSocket.addSubscription('/return/minilot/materialProperties', (message: IMessage) => {
			try {
				const data: MiniLotMaterialProperties = JSON.parse(message.body)
				if (data.requestId !== this.requestId) {
					return
				}

				// In the minilot case, the lot is derived both from the matchReferenceData (in MatchingResults.svelte as
				// prop for ReferenceEntity.svelte) and from the miniLot (in matching.getReferenceData() which runs in an $effect).
				// Since this can happen concurrently, we set the recyclingClasses in both matchReferenceData and miniLot.
				// matchReferenceData may not be resolved yet when this message arrives (or may be a pop), so guard the access.
				if (this.matchReferenceData?.objType === 'lot') {
					const lotReference = this.matchReferenceData as LotMatchPartner
					lotReference.recyclingClasses = data.recyclingClasses
				}
				if (this.miniLot) {
					this.miniLot.recyclingClasses = data.recyclingClasses
					this.miniLot.materialProperties = data.materialProperties
				}

				webSocket.onStreamMessage(
					'/return/minilot/materialProperties',
					`${data.requestId}:minilot:materialProperties`,
					data.endMessage
				)
			} catch (e) {
				console.error(e)
				throw new Error('Could not parse message body', { cause: e })
			}
		})
	}

	setReturnFreights(lotIds: string[]) {
		this.returnFreights.clear()
		lotIds.forEach((lotId) => {
			this.returnFreights.set(markerIdFor('lot', lotId), { id: lotId } as ReturnFreightData)
		})
	}

	/**
	 * Abort the in-flight matching run on the server. Safe to call repeatedly —
	 * a stale or unknown requestId is a no-op on the backend, and the local
	 * registry entry is dropped so {@link matchesLoading} flips immediately.
	 * The `:minilot` suffix is a local-only derived id (see {@link fetchMatchMarkerFromMiniLot}),
	 * so it gets released locally without a separate server call.
	 */
	#stopActiveMatching() {
		if (!this.requestId) return
		webSocket.stopStreamRequest('/stopMatchMarker', this.requestId)
		webSocket.markRequestEnd(`${this.requestId}:minilot`)
		webSocket.markRequestEnd(`${this.requestId}:minilot:materialProperties`)
	}

	// type-conditional fetch; use this instead of fetchMatchMarker directly
	refetch() {
		this.#stopActiveMatching()
		this.confirmedRadius = { from: this.radius.from, to: this.radius.to }
		this.isRadiusPending = false
		this.autoSelectFirst = true
		// old match set is invalidated by the new fetch; clearing here lets the
		// autoSelectFirst effect in MatchingResults pick the new first row, and
		// stops the route polyline from pointing at a partner that's no longer
		// in the result set.
		this.selectedMatch = undefined
		appState.route.clearCoordinates()
		if (this.fsm.current === 'result') {
			this.fsm.send('set_results')
		}
		if (this.miniLot) {
			this.fetchMatchMarkerFromMiniLot()
		} else {
			this.fetchMatchMarker()
		}
		void this.fitToRadius()
	}

	fetchMatchMarker() {
		this.matches.clear()
		const payload: GenericRequestMatchMarkerMessage = {
			lat: this.referenceMarker?.lat,
			lon: this.referenceMarker?.lon,
			referenceId: this.referenceId!,
			referenceType: this.selectedMarkerType!,
			profileId: this.profileId,
			radiusFrom: this.radius.from,
			radiusTo: this.radius.to
		}
		this.requestId = webSocket.startStreamRequest('/return/matches', '/matchMarker', payload)
	}

	fetchMatchMarkerFromMiniLot() {
		this.matches.clear()
		const payload: MiniLotRequestMatchMarkerMessage = {
			miniLot: miniLotToMatchRequest(this.miniLot!),
			referenceType: 'miniLot',
			radiusFrom: this.radius.from,
			radiusTo: this.radius.to
		}
		this.requestId = webSocket.startStreamRequest('/return/matches', '/matchMarker', payload)
		// The minilot stream uses a derived id so it doesn't overwrite '/return/matches'
		// in WebSock.activeRequests (the map holds one stream per requestId).
		webSocket.markRequestStart('/return/minilot/coordinates', `${this.requestId}:minilot`)
		webSocket.markRequestStart(
			'/return/minilot/materialProperties',
			`${this.requestId}:minilot:materialProperties`
		)
	}

	// effects
	// appState.handleFlyTo()
	// appState.handleFitBounds()

	destroy() {
		this.cleanupReferenceData?.()
		webSocket.unsubscribe('/return/matches')
		webSocket.unsubscribe('/return/minilot/coordinates')
		webSocket.unsubscribe('/return/minilot/materialProperties')
		// abort server-side workers and release local registry so busyStreams
		// drops immediately instead of waiting for the 30 s failsafe
		this.#stopActiveMatching()
		appState.route.clearCoordinates()
	}
}
